/**
 * The product page: variant picking, the quantity buttons, the artwork
 * brief, Add to cart and the FAQ list.
 *
 * Ported from components/product/ProductDetail.tsx (state and handlers),
 * lib/products.ts (perCupLabel, the per-cup maths), lib/dispatch.ts (the
 * proof and dispatch dates), lib/artworkFile.ts (the upload check),
 * lib/analytics.ts (the add_to_cart event) and
 * components/sections/FaqAccordion.tsx (the open/close rule).
 *
 * The markup is sections/product-main.liquid, which renders the selected
 * variant's state and a JSON script ([data-product-json]) with every
 * variant: id, sku, option1 (the wall), option2 (the rung), price in pence,
 * cups, cases, boxes, lead_time, artwork_fee and badge (erapack.badge, the
 * "Best value" highlight on a rung). This module reads it and
 * owns the panel from then on. Nothing is fetched: every price on the page
 * is in that JSON.
 *
 * Add to cart: with no file attached, the cup line (and the artwork-setup
 * line for sizes with the one-time fee) goes through /cart/add.js via
 * window.EraPackCart.add from cart.js, which opens the drawer. With a file,
 * the fee line is added first through /cart/add.js, then the product form
 * submits for real (the multipart POST to /cart/add is the only way Shopify
 * stores a file property) with return_to = this page + ?added=1; cart.js
 * opens the drawer on that load. Every add carries a fresh properties[_uid]
 * so identical adds stay separate lines, as the site's basket did.
 *
 * Lids (6, 8, 12 and 16oz hot cups and the 12 and 16oz clear cups,
 * snippets/lid-map.liquid): the JSON's `lids` holds the lid product that fits
 * this size and its runs (id, lids, price, mix). The "Add lids" tick-box is
 * off on every load; ticked, the lid run with the same count as the cups goes
 * into the Total, the with-VAT line and the add, in the same /cart/add.js
 * request as the cups. A clear cups mix gets one lid run per size half (5,000
 * lids with a 5,000 half: the lid product's mix-only run, never offered on
 * its own). Each lid line carries the hidden property _for = its cup line's
 * _uid, which cart.js uses to keep the lids on the cups' run and to drop them
 * with the cups.
 */

const GBP = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });
const EN_GB = new Intl.NumberFormat("en-GB");

/** "£0.106": three decimals, exactly as lib/products.ts perCupLabel. */
function perCupLabel(perCup) {
  return `£${Number(perCup).toFixed(3)}`;
}

/** "8.6p": the same figure said out loud (lib/products.ts penceLabel). */
function penceLabel(perCup) {
  return `${(Number(perCup) * 100).toFixed(1)}p`;
}

/** Pounds (a float) to "£1,234.56" (lib/format.ts gbp). */
function gbp(n) {
  return GBP.format(n);
}

const round2 = (n) => Math.round(n * 100) / 100;

/* ── lib/dispatch.ts ─────────────────────────────────────────────────────── */

const PROOF_CUTOFF_HOUR = 14;
const TZ = "Europe/London";
const LEAD_WORKING_DAYS = { "4 to 5 working days": 5, "2 to 3 weeks": 15 };

/* Lead time text to whole working days, the upper end of its window:
   "N to M working days" -> M, "N to M weeks" -> M x 5. The map above is the
   fallback for text that does not parse. */
function leadWorkingDays(leadTime) {
  const t = String(leadTime ?? "").toLowerCase();
  const m = t.match(/(\d+)(?:\s*(?:to|-|\u2013)\s*(\d+))?\s*(working\s+days?|days?|weeks?)/);
  if (m) {
    const upper = Number(m[2] ?? m[1]);
    return /week/.test(m[3]) ? upper * 5 : upper;
  }
  // Text with no number: 15 if it mentions weeks, else 5 (as snippets/lead-working-days.liquid).
  return LEAD_WORKING_DAYS[leadTime] ?? (/week/.test(t) ? 15 : 5);
}

/**
 * The "Lead time" line property, from the variant's lead_time (snippets/delivery-label.liquid):
 * "4 to 5 working days" -> "4 to 5 working days from proof approval"; "2 to 3 weeks" -> "2 to 3 weeks
 * from proof approval"; event cups' "2 days" -> "delivered 2 days after proof approval".
 */
function deliveryLabel(leadTime) {
  const t = String(leadTime ?? "").toLowerCase().trim();
  if (!t) return "";
  return /working|week/.test(t) ? `${t} from proof approval` : `delivered ${t} after proof approval`;
}

function londonParts(d) {
  const f = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    hour: "numeric",
    hourCycle: "h23",
    weekday: "short",
  });
  const parts = Object.fromEntries(f.formatToParts(d).map((p) => [p.type, p.value]));
  return { hour: Number(parts.hour), weekday: parts.weekday };
}

function isWeekend(d) {
  const { weekday } = londonParts(d);
  return weekday === "Sat" || weekday === "Sun";
}

/** Noon (UTC) on the London calendar date of `d`: 12:00 GMT or 13:00 BST, so
 *  stepping whole days never crosses midnight when the clocks change. */
function londonNoon(d) {
  const f = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, year: "numeric", month: "numeric", day: "numeric" });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), 12));
}

function addWorkingDays(from, days) {
  const d = londonNoon(from);
  let left = days;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (!isWeekend(d)) left--;
  }
  return d;
}

/**
 *   ordered before 2pm   proof the next working day
 *   ordered after 2pm    proof the working day after that
 *   dispatch             the proof day plus the cup's lead time in working
 *                        days (the longer end of its window: 5 for "4 to 5
 *                        working days", 15 for "2 to 3 weeks")
 */
export function dispatchEstimate(leadTime, now = new Date()) {
  const { hour } = londonParts(now);
  const beforeCutoff = !isWeekend(now) && hour < PROOF_CUTOFF_HOUR;
  const proofBy = addWorkingDays(now, beforeCutoff ? 1 : 2);
  const lead = leadWorkingDays(leadTime);
  const dispatchBy = addWorkingDays(proofBy, lead);
  return { beforeCutoff, proofBy, dispatchBy };
}

/** "Fri 3 Oct" */
export function shortDate(d) {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(d);
}

/* ── lib/analytics.ts ────────────────────────────────────────────────────── */

function track(name, params = {}) {
  const g = typeof window !== "undefined" ? window.gtag : null;
  if (typeof g !== "function") return;
  g("event", name, { currency: "GBP", ...params });
}

/* ── lib/artworkFile.ts ──────────────────────────────────────────────────── */

// Shopify's cart refuses a multipart add carrying a larger file with its
// "Something went wrong" page (tested 13 Oct 2026: 3.6 and 3.8 MB stored,
// 4.5 and 5.9 MB refused), so 3.5 MB is the ceiling.
const ARTWORK_MAX_BYTES = 3.5 * 1024 * 1024;

/**
 * Checks (and, for big rasters, shrinks) the chosen print file. Resolves
 * with the File to upload, never over 3.5 MB: the original when it is within
 * 3.5 MB, otherwise a PNG/JPG redrawn smaller (2400 px on the long side, then
 * smaller steps down to 1000 px) until it fits. Rejects with a message to show.
 */
const ARTWORK_TYPES = /^(image\/(png|jpe?g|svg\+xml|x-eps|eps)|application\/(pdf|postscript|illustrator|eps|x-eps))$/i;
const ARTWORK_EXTENSIONS = /\.(png|jpe?g|svg|pdf|ai|eps)$/i;
const ARTWORK_TOO_BIG = "That file is over 3.5MB. Export it smaller, or email it to hello@erapack.uk.";

function readArtworkFile(file) {
  // PDF, AI, EPS, SVG, PNG or JPG only (the cart stores all six: tested
  // 28 Oct 2026 with .ai and .eps), by MIME type or, when the browser gives none
  // (or a generic one), by the file name's extension.
  if (!ARTWORK_TYPES.test(file.type || "") && !ARTWORK_EXTENSIONS.test(file.name || "")) {
    return Promise.reject(new Error("PDF, AI, EPS, SVG, PNG or JPG only, or email it to us after you order."));
  }
  // Within 3.5 MB: uploaded exactly as chosen, full quality.
  if (file.size <= ARTWORK_MAX_BYTES) return Promise.resolve(file);
  const isRaster = /^image\/(png|jpe?g)$/.test(file.type);
  if (!isRaster) return Promise.reject(new Error(ARTWORK_TOO_BIG));

  // JPEG for photos, PNG for graphics with transparency.
  const type = file.type === "image/png" ? "image/png" : "image/jpeg";
  const draw = (img, side) =>
    new Promise((resolve) => {
      const scale = Math.min(1, side / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(resolve, type, type === "image/jpeg" ? 0.88 : undefined);
    });

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      URL.revokeObjectURL(url);
      for (const side of [2400, 1800, 1500, 1200, 1000]) {
        const blob = await draw(img, side);
        if (blob && blob.size > 0 && blob.size <= ARTWORK_MAX_BYTES) {
          resolve(new File([blob], file.name, { type, lastModified: Date.now() }));
          return;
        }
      }
      reject(new Error(ARTWORK_TOO_BIG));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That image could not be opened — try a PNG or JPG."));
    };
    img.src = url;
  });
}

/** A theme asset's srcset: 300, 600 and its own 1000px width. */
function assetSrcset(url) {
  const u = String(url || "");
  if (!u) return "";
  const sep = u.includes("?") ? "&" : "?";
  return `${u}${sep}width=300 300w, ${u}${sep}width=600 600w, ${u} 1000w`;
}

/* ── the rough logo preview (the price page's "See your cup") ───────────── */

/** Lighten (amount > 0) or darken a hex colour, as calculator.js shade(). */
function shadeHex(hex, amount) {
  const m = hex.replace("#", "");
  const n = parseInt(m.length === 3 ? m.replace(/./g, "$&$&") : m, 16);
  const ch = (v) => Math.max(0, Math.min(255, Math.round(v + (amount < 0 ? v : 255 - v) * amount)));
  return `rgb(${ch((n >> 16) & 255)},${ch((n >> 8) & 255)},${ch(n & 255)})`;
}

/**
 * Draws a plain white cup on the canvas with the logo wrapped round its
 * body, the same drawing as the price page's preview (calculator.js
 * setupCupPreview paint()). Nothing is uploaded for it: the logo is read
 * in the browser. A drawn cup, never a photo, so it reads as a rough idea.
 */
function drawCupWithLogo(canvas, logoUrl) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;
  const colour = "#ffffff";
  ctx.clearRect(0, 0, W, H);
  const topW = W * 0.62;
  const botW = W * 0.46;
  const top = H * 0.16;
  const bot = H * 0.9;
  const cx = W / 2;
  const ry = 0.13;
  const body = new Path2D();
  body.moveTo(cx - topW / 2, top);
  body.lineTo(cx - botW / 2, bot);
  body.ellipse(cx, bot, botW / 2, (botW / 2) * ry, 0, Math.PI, 0, true);
  body.lineTo(cx + topW / 2, top);
  body.ellipse(cx, top, topW / 2, (topW / 2) * ry, 0, 0, Math.PI, false);
  body.closePath();
  ctx.save();
  ctx.fillStyle = "rgba(0,0,0,0.10)";
  ctx.filter = "blur(10px)";
  ctx.beginPath();
  ctx.ellipse(cx + 6, bot + 4, botW * 0.62, botW * 0.11, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  const g = ctx.createLinearGradient(cx - topW / 2, 0, cx + topW / 2, 0);
  g.addColorStop(0, shadeHex(colour, -0.1));
  g.addColorStop(0.28, shadeHex(colour, 0.06));
  g.addColorStop(0.62, colour);
  g.addColorStop(1, shadeHex(colour, -0.2));
  ctx.fillStyle = g;
  ctx.fill(body);
  const finish = () => {
    ctx.globalAlpha = 1;
    ctx.lineWidth = 3;
    ctx.strokeStyle = "rgba(0,0,0,0.12)";
    ctx.beginPath();
    ctx.ellipse(cx, top, topW / 2, (topW / 2) * ry, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.ellipse(cx, top, topW / 2 - 3, (topW / 2) * ry - 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(0,0,0,0.06)";
    ctx.beginPath();
    ctx.ellipse(cx, top, topW / 2 - 9, (topW / 2) * ry - 5, 0, 0, Math.PI * 2);
    ctx.fill();
  };
  if (!logoUrl) {
    finish();
    return;
  }
  const img = new Image();
  img.onload = () => {
    const bandTop = top + (bot - top) * 0.3;
    const bandH = (bot - top) * 0.36;
    const lh = Math.min(bandH, img.height * ((topW * 0.62) / img.width));
    const strips = 96;
    const arc = Math.PI * 0.72;
    const radius = topW * 0.46;
    const xAt = (i) => cx + Math.sin((i / strips - 0.5) * arc) * radius;
    ctx.save();
    ctx.clip(body);
    ctx.globalCompositeOperation = "multiply";
    for (let i = 0; i < strips; i++) {
      const theta = ((i + 0.5) / strips - 0.5) * arc;
      const x0 = xAt(i);
      const x1 = xAt(i + 1);
      const y = bandTop + (bandH - lh) / 2 + Math.abs(theta) * 6;
      ctx.globalAlpha = 1 - Math.abs(theta) * 0.22;
      ctx.drawImage(img, (i / strips) * img.width, 0, img.width / strips, img.height, x0, y, x1 - x0 + 0.75, lh);
    }
    ctx.restore();
    finish();
  };
  img.onerror = () => finish();
  img.src = logoUrl;
}

/* ── the buy panel ───────────────────────────────────────────────────────── */

function wallKeyOf(title) {
  return /double/i.test(String(title || "")) ? "double" : "single";
}

function setupProduct(root) {
  const jsonEl = root.querySelector("[data-product-json]");
  if (!jsonEl) return;
  let data;
  try {
    data = JSON.parse(jsonEl.textContent);
  } catch {
    return;
  }

  const form = root.querySelector("[data-product-form]");
  const idInput = root.querySelector("[data-variant-id]");
  const stage = root.querySelector("[data-stage-img]");
  const mediaDesktop = root.querySelector(".custom-main-product-media-desktop");
  const subtitle = root.querySelector("[data-subtitle]");
  const list = root.querySelector("[data-qty-list]");
  const toggle = root.querySelector("[data-price-toggle]");
  const totalPrice = root.querySelector("[data-total-price]");
  const totalVat = root.querySelector("[data-total-vat]");
  const totalDates = root.querySelector("[data-total-dates]");
  const feeNote = root.querySelector("[data-fee-note]");
  const deliveryAmount = root.querySelector("[data-delivery-amount]");
  const deliveryBoxes = root.querySelector("[data-delivery-boxes]");
  const atc = root.querySelector("[data-atc]");
  const wallInputs = Array.from(root.querySelectorAll("[data-wall-input]"));

  // An option value may arrive as a product_option_value object ({ name })
  // rather than a string: every rung and variant option is read as its name.
  const optName = (x) => (x && typeof x === "object" ? String(x.name ?? x.value ?? "") : String(x ?? ""));
  const rungs = (Array.isArray(data.rungs) ? data.rungs : []).map(optName);
  const walls = Array.isArray(data.walls) && data.walls.length ? data.walls : [{ key: "single", title: "Single wall", frames: [] }];
  const allVariants = (Array.isArray(data.variants) ? data.variants : []).map((v) => ({
    ...v,
    option1: optName(v.option1),
    option2: optName(v.option2),
  }));
  // The clear cups' 5,000 runs (mix: true) are only ever half of a mix.
  const variants = allVariants.filter((v) => !v.mix);
  const partner = data.partner && Array.isArray(data.partner.variants) ? data.partner : null;
  const mixInputs = Array.from(root.querySelectorAll("[data-mix-input]"));
  const mixLines = root.querySelector("[data-mix-lines]");
  const mixHint = root.querySelector("[data-mix-hint]");
  const oneHint = root.querySelector("[data-one-hint]");
  const qtyLegend = root.querySelector("[data-qty-legend]");
  const stageMix = root.querySelector("[data-stage-mix]");
  const atcLabel = atc ? atc.textContent : "";
  const lids =
    data.lids && Array.isArray(data.lids.variants) && data.lids.variants.length ? data.lids : null;
  const lidsWrap = root.querySelector("[data-lids]");
  const lidsInput = root.querySelector("[data-lids-input]");
  const lidsNote = root.querySelector("[data-lids-note]");

  const wallByKey = (key) => walls.find((w) => w.key === key) || walls[0];
  // A variant's group (option1) to its key: the walls (or print choices,
  // e.g. "2 colours" -> "2-colours") are matched by title first; a title the
  // list does not hold falls back to the single/double rule.
  const keyOf = (title) => {
    const w = walls.find((x) => String(x.title) === String(title));
    return w ? w.key : wallKeyOf(title);
  };
  const variantsFor = (wallKey) => {
    const w = wallByKey(wallKey);
    const byRung = new Map(
      variants.filter((v) => keyOf(v.option1) === w.key).map((v) => [String(v.option2), v]),
    );
    // By cups, not the Quantity option's order: Shopify lists a value where
    // it first appears, so the double wall's 2,500 comes after every single
    // wall rung there. Only the rungs the option lists, when it lists them.
    const ladder = rungs.length
      ? rungs.map((r) => byRung.get(String(r)) || null).filter(Boolean)
      : [...byRung.values()];
    return ladder.sort((a, b) => a.cups - b.cups);
  };

  /* state */
  let wall = data.wall || walls[0].key;
  let rungIndex = 0;
  let frame = 0;
  let showPrices = false;
  let adding = false;
  let mixing = false;
  let mixIndex = 0;
  let pendingMix = null; // a ?variant= 5,000 run, placed once the packs are known
  // Lids are never on when the page opens (a ?qty= or ?wall= link included);
  // a browser that restores form state is overruled here.
  let lidsOn = false;
  if (lidsInput) lidsInput.checked = false;

  // Shop cards deep-link a wall (?wall=double); a Shopify link may carry
  // ?variant=<id>. The wall from the URL wins, as the component did. The
  // landing calculator adds ?qty=<cups>: that run on the chosen wall, or the
  // nearest one it sells (a tie goes to the smaller), unless ?variant= placed
  // the rung already.
  try {
    const params = new URLSearchParams(window.location.search);
    const wantedWall = params.get("wall");
    if (wantedWall && walls.some((w) => w.key === wantedWall)) wall = wantedWall;
    if (partner && params.get("mix") === "1") mixing = true;
    const wantedVariant = params.get("variant");
    // A basket line of a mix links its 5,000 run: open the mix on that print and pack.
    const mixV = wantedVariant && partner ? allVariants.find((x) => x.mix && String(x.id) === String(wantedVariant)) : null;
    let placed = false;
    if (mixV) {
      mixing = true;
      if (!wantedWall) wall = keyOf(mixV.option1);
      pendingMix = mixV;
      placed = true;
    }
    if (wantedVariant && !mixV) {
      const v = variants.find((x) => String(x.id) === String(wantedVariant));
      if (v) {
        if (!wantedWall) wall = keyOf(v.option1);
        const i = variantsFor(wall).findIndex((x) => x.id === v.id);
        if (i >= 0) rungIndex = i;
        placed = true;
      }
    }
    const wantedCups = Number(params.get("qty"));
    if (!placed && wantedCups > 0) {
      const l = variantsFor(wall);
      let best = -1;
      l.forEach((x, i) => {
        const d = Math.abs(x.cups - wantedCups);
        const bestD = best >= 0 ? Math.abs(l[best].cups - wantedCups) : Infinity;
        if (d < bestD || (d === bestD && x.cups < l[best].cups)) best = i;
      });
      if (best >= 0) rungIndex = best;
    }
  } catch {
    /* no URL access: keep the Liquid defaults */
  }

  const ladder = () => variantsFor(wall);
  const current = () => {
    const l = ladder();
    return l[Math.min(rungIndex, l.length - 1)] || null;
  };
  const perCup = (v) => (v && v.cups > 0 ? v.price / 100 / v.cups : 0);
  /**
   * The lid run of `cups` lids, or null. A mix-only run (the clear lids'
   * 5,000) only when `half` is set: it goes with a 5,000 mix half, never alone.
   */
  const lidRunOf = (cups, half) =>
    lids
      ? lids.variants.find(
          (x) => Number(x.lids) === Number(cups) && x.available !== false && (half || !x.mix),
        ) || null
      : null;
  /** The lid run for a cup run: the same count ("2,500 lids" for 2,500 cups), or null. */
  const lidFor = (v) => (v && !mixing ? lidRunOf(v.cups, false) : null);
  /** The lid run to add with the cups: only while the box is ticked. */
  const chosenLid = (v) => (lidsOn ? lidFor(v) : null);
  /** A mix pack's lids: one run per size half, [a, b], or null when either is missing. */
  const lidsForPack = (pack) => {
    if (!pack) return null;
    const a = lidRunOf(pack.a.cups, true);
    const b = lidRunOf(pack.b.cups, true);
    return a && b ? [a, b] : null;
  };
  const cupsLabel = (v) => (v ? EN_GB.format(v.cups) : "");

  /**
   * The mix packs for the chosen print: one per run both clear sizes sell
   * (5,000 + 5,000, 10,000 + 10,000 …), each a pair of variants, this size's
   * first. Each size is priced on its own run, as when bought alone.
   */
  const mixPacks = () => {
    if (!partner) return [];
    const w = wallByKey(wall);
    const mine = allVariants.filter((v) => keyOf(v.option1) === w.key);
    const theirs = partner.variants.filter((v) => String(v.option1) === String(w.title));
    return mine
      .map((a) => ({ a, b: theirs.find((x) => Number(x.cups) === Number(a.cups)) }))
      .filter((p) => p.b)
      .sort((x, y) => x.a.cups - y.a.cups);
  };
  const currentPack = () => {
    const packs = mixPacks();
    return packs[Math.min(mixIndex, packs.length - 1)] || null;
  };
  if (pendingMix) {
    const i = mixPacks().findIndex((p) => p.a.id === pendingMix.id);
    if (i >= 0) mixIndex = i;
  }

  function renderMedia() {
    const w = wallByKey(wall);
    const frames = Array.isArray(w.frames) ? w.frames.filter(Boolean) : [];
    if (!frames.length) return;
    if (frame >= frames.length) frame = 0;
    if (stage && stage.getAttribute("src") !== frames[frame]) {
      stage.setAttribute("src", frames[frame]);
      // The same widths the Liquid srcset lists (the CDN resizes on &width=).
      if (stage.hasAttribute("srcset")) stage.setAttribute("srcset", assetSrcset(frames[frame]));
    }
    let thumbs = root.querySelector("[data-thumbs]");
    if (frames.length > 1) {
      if (!thumbs) {
        thumbs = document.createElement("div");
        thumbs.className = "custom-main-product-thumbs";
        thumbs.setAttribute("data-thumbs", "");
        if (mediaDesktop) mediaDesktop.insertBefore(thumbs, mediaDesktop.firstChild);
      }
      const buttons = Array.from(thumbs.querySelectorAll("[data-thumb]"));
      frames.forEach((src, i) => {
        let b = buttons[i];
        if (!b) {
          b = document.createElement("button");
          b.type = "button";
          b.className = "custom-main-product-thumb-img";
          b.setAttribute("data-thumb", String(i));
          thumbs.appendChild(b);
        }
        b.setAttribute("aria-current", i === frame ? "true" : "false");
        b.setAttribute("aria-label", `View image ${i + 1}`);
        b.style.backgroundImage = `url(${src})`;
        b.style.backgroundSize = "cover";
        b.style.backgroundPosition = "center";
      });
      buttons.slice(frames.length).forEach((b) => b.remove());
    } else if (thumbs) {
      thumbs.remove();
    }
  }

  /**
   * The short list: the quantities shown before "More quantities" (1,000,
   * 2,000 or 2,500, 5,000 and 10,000 cups: a wall sells 2,000 or 2,500, not
   * both), and any rung with a badge. A ladder without them shows its first
   * four instead.
   */
  const CORE_CUPS = [1000, 2000, 2500, 5000, 10000];
  const badgeText = (v) => String((v && v.badge) ?? "").trim();
  // A short ladder (six rungs or fewer: the clear cups' 10,000 to 100,000)
  // shows every quantity and no "More quantities" link.
  const isCore = (v, l) =>
    l.length <= 6 ||
    !!badgeText(v) ||
    (l.some((x) => CORE_CUPS.includes(x.cups)) ? CORE_CUPS.includes(v.cups) : l.indexOf(v) < 4);

  /**
   * A rung's highlight: its badge ("Best value"), then the saving per cup on
   * the wall's first rung, its smallest run ("Save 5%"), left out under 1 %.
   * Empty for a rung with no badge. The same sums as product-main.liquid.
   */
  const badgeParts = (v, l) => {
    const text = badgeText(v);
    if (!text) return [];
    const base = l.reduce((m, x) => (x.cups > 0 && (!m || x.cups < m.cups) ? x : m), null);
    const save =
      base && base.price > 0 && v.cups > 0
        ? Math.round((1 - (v.price * base.cups) / (v.cups * base.price)) * 100)
        : 0;
    return save >= 1 ? [text, `Save ${save}%`] : [text];
  };

  /** Adds, updates or removes the pill and the saving line on a reused chip. */
  function setBadge(b, parts) {
    const [text, save] = parts;
    b.classList.toggle("pm-a__chip--badge", !!text);
    let saveEl = b.querySelector(":scope > .pm-a__save");
    let pill = b.querySelector(":scope > .pm-a__badge");
    if (save) {
      if (!saveEl) {
        saveEl = document.createElement("em");
        saveEl.className = "pm-a__save";
        b.insertBefore(saveEl, pill);
      }
      saveEl.textContent = save;
    } else if (saveEl) {
      saveEl.remove();
    }
    if (text) {
      if (!pill) {
        pill = document.createElement("small");
        pill.className = "pm-a__badge";
        b.appendChild(pill);
      }
      pill.textContent = text;
    } else if (pill) {
      pill.remove();
    }
  }

  /** The quantity chips: one button per rung, the price per cup under it. */
  function renderMixList() {
    const packs = mixPacks();
    const cur = currentPack();
    const existing = Array.from(list.querySelectorAll("[data-qty-row]"));
    const focusedRow = list.contains(document.activeElement)
      ? document.activeElement.getAttribute("data-qty-row")
      : null;
    packs.forEach((p, i) => {
      let b = existing[i];
      if (!b) {
        b = document.createElement("button");
        b.type = "button";
        b.append(document.createElement("strong"), document.createElement("span"));
        list.appendChild(b);
      }
      b.className = "pm-a__chip";
      b.setAttribute("aria-pressed", cur && p.a.id === cur.a.id ? "true" : "false");
      b.setAttribute("data-qty-row", String(i));
      b.setAttribute("data-variant", String(p.a.id));
      b.querySelector("strong").textContent = `${EN_GB.format(p.a.cups)} + ${EN_GB.format(p.b.cups)}`;
      b.querySelector("span").textContent = `${EN_GB.format(p.a.cups + Number(p.b.cups))} cups`;
      setBadge(b, []);
    });
    existing.slice(packs.length).forEach((b) => b.remove());
    if (focusedRow !== null && !list.contains(document.activeElement)) {
      const again = list.querySelector(`[data-qty-row="${focusedRow}"]`);
      if (again) again.focus();
    }
    list.classList.add("pm-a__qty--mix");
    list.classList.remove("pm-a__qty--badges");
    if (toggle) toggle.hidden = true;
  }

  function renderList() {
    if (!list) return;
    list.classList.toggle("pm-a__qty--mix", mixing);
    if (mixing) {
      renderMixList();
      return;
    }
    const l = ladder();
    const cur = current();
    list.classList.toggle("pm-a__qty--badges", variants.some((v) => badgeText(v)));
    // Update the chips in place so the one just pressed keeps keyboard and
    // screen-reader focus; only add or remove chips when the ladder length
    // changes. A chip that changes wall takes or drops the badge with it.
    const focusedRow = list.contains(document.activeElement)
      ? document.activeElement.getAttribute("data-qty-row")
      : null;
    const existing = Array.from(list.querySelectorAll("[data-qty-row]"));
    l.forEach((v, i) => {
      let b = existing[i];
      if (!b) {
        b = document.createElement("button");
        b.type = "button";
        b.append(document.createElement("strong"), document.createElement("span"));
        list.appendChild(b);
      }
      b.className = `pm-a__chip${isCore(v, l) ? "" : " pm-a__chip--extra"}`;
      b.setAttribute("aria-pressed", cur && v.id === cur.id ? "true" : "false");
      b.setAttribute("data-qty-row", String(i));
      b.setAttribute("data-variant", String(v.id));
      b.querySelector("strong").textContent = cupsLabel(v);
      b.querySelector("span").textContent = `${perCupLabel(perCup(v))} a cup`;
      setBadge(b, badgeParts(v, l));
    });
    existing.slice(l.length).forEach((b) => b.remove());
    if (focusedRow !== null && !list.contains(document.activeElement)) {
      const again = list.querySelector(`[data-qty-row="${focusedRow}"]`);
      if (again) again.focus();
    }
    list.classList.toggle("is-open", showPrices);
    // The Liquid already leaves out the link (and its " · ") on a short ladder.
    if (toggle) toggle.hidden = !l.some((x) => !isCore(x, l));
    if (toggle) {
      toggle.setAttribute("aria-expanded", showPrices ? "true" : "false");
      toggle.textContent = showPrices ? "Fewer quantities" : "More quantities";
    }
  }

  /** The two lines of a mix under the packs: photo, size, cups, price. */
  function renderMixLines(pack) {
    if (!mixLines) return;
    mixLines.hidden = !mixing || !pack;
    if (!mixing || !pack) return;
    const rows = [
      { img: stage ? stage.getAttribute("src") : "", name: `${data.size} ${data.kind || "clear cups"}`, v: pack.a },
      { img: partner.image, name: `${partner.size} ${data.kind || "clear cups"}`, v: pack.b },
    ];
    // Smaller size first, as the switch reads ("Mix 12oz + 16oz").
    if (parseInt(partner.size, 10) < parseInt(data.size, 10)) rows.reverse();
    mixLines.replaceChildren(
      ...rows.map((r) => {
        const row = document.createElement("div");
        row.className = "pm-a__mix-row";
        const img = document.createElement("img");
        img.src = r.img;
        img.alt = "";
        img.width = 40;
        img.height = 48;
        const text = document.createElement("div");
        const name = document.createElement("strong");
        name.textContent = r.name;
        const cups = document.createElement("span");
        cups.textContent = `${EN_GB.format(r.v.cups)} cups`;
        text.append(name, cups);
        const price = document.createElement("b");
        price.textContent = gbp(r.v.price / 100);
        row.append(img, text, price);
        return row;
      }),
    );
  }

  function render() {
    const l = ladder();
    const v = current();
    if (!v) return;
    root.classList.toggle("pm-a--mix", mixing);
    mixInputs.forEach((input) => {
      input.checked = input.value === (mixing ? "mix" : "one");
    });
    if (stageMix) stageMix.hidden = !mixing;
    if (mixHint) mixHint.hidden = !mixing;
    if (oneHint) oneHint.hidden = mixing;
    if (qtyLegend) qtyLegend.textContent = mixing ? "Quantity, per size" : "Quantity";
    if (atc) atc.textContent = mixing ? "Add both to basket" : atcLabel;
    const pack = mixing ? currentPack() : null;
    renderMixLines(pack);
    const last = l[l.length - 1];
    const w = wallByKey(wall);

    wallInputs.forEach((input) => {
      input.checked = input.value === w.key;
    });
    root.setAttribute("data-wall", w.key);
    if (idInput) idInput.value = String(v.id);

    // A short ladder (the clear cups) has one price per cup at every quantity.
    // The entry run first, then the volume rate (as product-main.liquid renders it).
    const first = l[0];
    if (subtitle && last) {
      subtitle.textContent = first && first !== last
        ? `${perCupLabel(perCup(first))} a cup at ${cupsLabel(first)}, down to ${perCupLabel(perCup(last))} at ${cupsLabel(last)}`
        : `${perCupLabel(perCup(last))} a cup at ${cupsLabel(last)}`;
    }

    renderList();
    renderMedia();

    // Lids: shown only where a run of the same count exists (on a mix, one
    // for each size); the note is their exact price ("+ £62.48 for 2,500
    // lids", "+ £300.00 for 10,000 lids, 5,000 a size").
    const lidRuns = pack ? lidsForPack(pack) : lidFor(v) ? [lidFor(v)] : null;
    if (lidsWrap) lidsWrap.hidden = !lidRuns;
    if (lidsInput) lidsInput.checked = lidsOn && !!lidRuns;
    if (lidsNote) {
      if (lidRuns) {
        const lidCount = lidRuns.reduce((n, x) => n + Number(x.lids), 0);
        const lidPrice = lidRuns.reduce((n, x) => n + Number(x.price), 0);
        const each = lidRuns.length > 1 ? `, ${EN_GB.format(lidRuns[0].lids)} a size` : "";
        lidsNote.textContent = `+ ${gbp(lidPrice / 100)} for ${EN_GB.format(lidCount)} lids${each}`;
      } else {
        lidsNote.textContent = "";
      }
    }
    const lidsChosen = lidsOn && lidRuns ? lidRuns : [];
    const lidsTotal = lidsChosen.reduce((n, x) => n + Number(x.price), 0) / 100;

    const subtotal = (pack ? (pack.a.price + Number(pack.b.price)) / 100 : v.price / 100) + lidsTotal;
    const fee = pack ? 0 : Number(v.artwork_fee) || 0;
    const vat = round2((subtotal + fee) * 0.2); // VAT on the cups, the lids and the artwork setup, as checkout charges
    const totalExCarriage = round2(subtotal + vat + fee);
    if (totalVat) {
      totalVat.textContent = `${gbp(totalExCarriage)} with VAT`;
    }
    if (feeNote) feeNote.hidden = !(fee > 0);
    if (totalDates) {
      // No delivery price or lead time here: the basket and checkout show both.
      totalDates.textContent = "Free proof the same day";
    }
    if (totalPrice) totalPrice.textContent = gbp(subtotal + fee);
    // Delivery to mainland UK: every box of the run (both halves of a mix)
    // and of the ticked lids, at the rate a box, as the basket charges it.
    if (deliveryAmount) {
      const boxesOf = (x) => Math.max(0, Number(x && x.boxes) || 0);
      const cupBoxes = pack ? boxesOf(pack.a) + boxesOf(pack.b) : boxesOf(v);
      const boxes = cupBoxes + lidsChosen.reduce((n, x) => n + boxesOf(x), 0);
      const rate = Number(data.carriage) || 4;
      if (boxes > 0) {
        deliveryAmount.textContent = gbp(boxes * rate);
        if (deliveryBoxes) deliveryBoxes.textContent = `(${EN_GB.format(boxes)} ${boxes === 1 ? "box" : "boxes"} × ${gbp(rate)})`;
      }
    }
    if (sticky) sticky.update(gbp(totalExCarriage));
    if (atc) atc.disabled = adding;
  }

  /* handlers */
  wallInputs.forEach((input) => {
    input.addEventListener("change", () => {
      if (!input.checked) return;
      // Any listed choice: single/double wall, or 1/2 colour print.
      wall = walls.some((w) => w.key === input.value) ? input.value : walls[0].key;
      frame = 0; // the chosen wall's own photo leads, as on first paint
      rungIndex = Math.min(rungIndex, Math.max(0, ladder().length - 1));
      render();
    });
  });

  mixInputs.forEach((input) => {
    input.addEventListener("change", () => {
      if (!input.checked) return;
      mixing = input.value === "mix";
      render();
    });
  });

  if (lidsInput) {
    lidsInput.addEventListener("change", () => {
      lidsOn = lidsInput.checked;
      render();
    });
  }

  root.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const thumb = t.closest("[data-thumb]");
    if (thumb && root.contains(thumb)) {
      frame = Number(thumb.getAttribute("data-thumb")) || 0;
      renderMedia();
      return;
    }
    const row = t.closest("[data-qty-row]");
    if (row && root.contains(row)) {
      if (mixing) mixIndex = Number(row.getAttribute("data-qty-row")) || 0;
      else rungIndex = Number(row.getAttribute("data-qty-row")) || 0;
      render();
      return;
    }
    if (t.closest("[data-price-toggle]")) {
      showPrices = !showPrices;
      renderList();
    }
  });

  /* the artwork brief */
  const tile = root.querySelector("[data-artwork-tile]");
  const fileInput = root.querySelector("[data-artwork-input]");
  const nameEl = root.querySelector("[data-artwork-name]");
  const hintEl = root.querySelector("[data-artwork-hint]");
  let artworkError = null;

  function renderArtwork() {
    const file = fileInput && fileInput.files && fileInput.files[0];
    const has = !!(file && file.size);
    if (tile) {
      tile.classList.toggle("has-file", has);
      tile.classList.toggle("has-error", !!artworkError);
    }
    const ds = fileInput ? fileInput.dataset : {};
    if (nameEl) nameEl.textContent = has ? file.name : ds.emptyLabel || "Upload your logo or artwork";
    if (hintEl) {
      hintEl.textContent =
        artworkError ||
        (has
          ? ds.attachedHint || "Attached to this order. Click to swap it."
          : ds.emptyHint || "PDF, AI, EPS or SVG prints sharpest. PNG or JPG work too, up to 3.5 MB");
    }
  }

  if (fileInput) {
    fileInput.addEventListener("change", () => {
      const file = fileInput.files && fileInput.files[0];
      if (!file) return;
      artworkError = null;
      renderArtwork();
      readArtworkFile(file).then(
        (result) => {
          if (result !== file) {
            let swapped = false;
            try {
              const dt = new DataTransfer();
              dt.items.add(result);
              fileInput.files = dt.files;
              swapped = fileInput.files[0] === result || fileInput.files[0].size === result.size;
            } catch {
              swapped = false;
            }
            // A browser that cannot swap the file would upload the big
            // original, which Shopify refuses: ask for a smaller export.
            if (!swapped) {
              fileInput.value = "";
              artworkError = ARTWORK_TOO_BIG;
              renderArtwork();
              return;
            }
          }
          artworkError = null;
          renderArtwork();
        },
        (err) => {
          fileInput.value = "";
          artworkError = err && err.message ? err.message : "Could not read that file — try again.";
          renderArtwork();
        },
      );
    });
  }

  /* add to cart */

  /** A per-add id, so two identical adds stay two lines (the site's uid). */
  const newUid = () =>
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

  /** The artwork-setup companion line for a size that carries the fee. */
  function feeItem(v) {
    const fee = Number(v.artwork_fee) || 0;
    const feeVariant = Number(data.artworkVariant) || 0;
    if (fee <= 0 || !feeVariant) return null;
    const w = wallByKey(wall);
    return { id: feeVariant, quantity: 1, properties: { For: `${data.title} · ${w.title}` } };
  }

  /**
   * A mix: both clear sizes' lines in one request, the same print, logo and
   * notes on each. A logo file travels as a multipart POST to /cart/add.js
   * (items[n][properties][Artwork] per line, so each line keeps its own copy),
   * which Shopify accepts where the JSON API cannot carry a file; then the
   * basket is re-read and opened.
   */
  async function addMix(notesText, file) {
    const pack = currentPack();
    if (!pack) throw new Error("Pick a quantity for the mix.");
    if (file && file.size > ARTWORK_MAX_BYTES) throw new Error(ARTWORK_TOO_BIG);
    const lines = [pack.a, pack.b].map((v) => {
      const properties = { _uid: newUid() };
      const delivery = deliveryLabel(v.lead_time);
      if (delivery) properties["Lead time"] = delivery;
      if (notesText) properties["Design preferences"] = notesText;
      return { id: Number(v.id), quantity: 1, properties };
    });
    // Lids, ticked: one lid line per size half, each tied to its cup line.
    const packLids = lidsOn ? lidsForPack(pack) : null;
    const lidItems = packLids
      ? packLids.map((x, i) => ({ id: Number(x.id), quantity: 1, properties: { _for: lines[i].properties._uid } }))
      : [];
    track("add_to_cart", {
      currency: "GBP",
      value: round2((pack.a.price + Number(pack.b.price) + (packLids ? packLids.reduce((n, x) => n + Number(x.price), 0) : 0)) / 100),
      items: [
        ...[pack.a, pack.b].map((v, i) => ({
          item_id: v.sku || String(v.id),
          item_name: `${i === 0 ? data.size : partner.size} ${data.kind || "clear cups"}`,
          item_variant: `${wallByKey(wall).title} / ${EN_GB.format(v.cups)} cups (mix)`,
          price: round2(v.price / 100),
          quantity: 1,
        })),
        ...(packLids || []).map((x) => ({
          item_id: x.sku || String(x.id),
          item_name: lids.title || "Lids",
          item_variant: `${EN_GB.format(x.lids)} lids`,
          price: round2(x.price / 100),
          quantity: 1,
        })),
      ],
    });
    const cart = window.EraPackCart;
    if (!file) {
      if (cart && typeof cart.add === "function") {
        await cart.add([...lines, ...lidItems]);
        if (typeof cart.open === "function") cart.open();
        return;
      }
    }
    // The file is uploaded once, with the first line (two copies in one
    // request could pass Shopify's upload limit); the second line then
    // carries the stored file's address.
    const postLines = async (fd) => {
      for (let tries = 0; ; tries += 1) {
        const res = await fetch("/cart/add.js", { method: "POST", body: fd, headers: { Accept: "application/json" } });
        if (res.status === 429 && tries < 2) {
          await new Promise((r) => setTimeout(r, 1500));
          continue;
        }
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j.description || j.message || "Could not add the mix to the basket. Please try again.");
        return j;
      }
    };
    const formOf = (list, withFile) => {
      const fd = new FormData();
      list.forEach((line, i) => {
        const k = `items[${i}]`;
        fd.append(`${k}[id]`, String(line.id));
        fd.append(`${k}[quantity]`, "1");
        for (const [name, value] of Object.entries(line.properties)) fd.append(`${k}[properties][${name}]`, value);
        if (withFile) fd.append(`${k}[properties][Artwork]`, file, file.name);
      });
      return fd;
    };
    const first = await postLines(formOf([lines[0]], true));
    const added = Array.isArray(first.items) ? first.items[0] : first;
    const stored = added && added.properties ? added.properties.Artwork : "";
    if (typeof stored === "string" && stored.startsWith("https://")) {
      lines[1].properties.Artwork = stored;
      await postLines(formOf([lines[1]], false));
    } else {
      await postLines(formOf([lines[1]], true));
    }
    if (lidItems.length) {
      const fd = new FormData();
      lidItems.forEach((line, i) => {
        fd.append(`items[${i}][id]`, String(line.id));
        fd.append(`items[${i}][quantity]`, "1");
        fd.append(`items[${i}][properties][_for]`, line.properties._for);
      });
      await postLines(fd);
    }
    if (cart && typeof cart.refresh === "function") {
      await cart.refresh();
      if (typeof cart.open === "function") cart.open();
    } else {
      window.location.href = data.cartUrl || "/cart";
    }
  }

  async function addToCart(e) {
    if (e) e.preventDefault();
    if (adding) return;
    const v = current();
    if (!v || !form) return;

    const file = fileInput && fileInput.files && fileInput.files[0];
    const hasFile = !!(file && file.size);
    const notes = root.querySelector("[data-artwork-notes]");
    const text = notes ? String(notes.value || "").trim() : "";
    const uid = newUid();
    const fee = feeItem(v);
    // The lids for this cup line: the run of the same count, tied to the cup
    // line by its _uid (cart.js keeps them together from here on).
    const lid = mixing ? null : chosenLid(v);
    const lidItem = lid ? { id: Number(lid.id), quantity: 1, properties: { _for: uid } } : null;

    adding = true;
    render();
    if (!mixing) track("add_to_cart", {
      currency: "GBP",
      value: round2(v.price / 100 + (Number(v.artwork_fee) || 0) + (lid ? lid.price / 100 : 0)),
      items: [
        {
          item_id: v.sku || String(v.id),
          item_name: `${data.size} ${data.kind || "paper cups"}`,
          item_variant: `${wallByKey(wall).title} / ${v.option2 || `${cupsLabel(v)} cups`}`,
          price: round2(v.price / 100),
          quantity: 1,
        },
        ...(lid
          ? [
              {
                item_id: lid.sku || String(lid.id),
                item_name: lids.title || "Lids",
                item_variant: `${EN_GB.format(lid.lids)} lids`,
                price: round2(lid.price / 100),
                quantity: 1,
              },
            ]
          : []),
      ],
    });

    try {
      if (mixing) {
        await addMix(text, hasFile ? file : null);
        adding = false;
        render();
        return;
      }
      if (hasFile) {
        // Shopify stores a file property only from the product form's own
        // multipart POST to /cart/add. The form submits for real and comes
        // back here with ?added=1; cart.js then adds the artwork-setup fee
        // line the cup needs (its reconcile on load) and opens the drawer,
        // so a refused upload never leaves a fee behind without its cup.
        if (file.size > ARTWORK_MAX_BYTES) throw new Error(ARTWORK_TOO_BIG);
        // The lids go in first, tied to this add's _uid: if the upload is
        // refused, cart.js drops them as lids without their cups.
        if (lidItem) {
          const c = window.EraPackCart;
          if (c && typeof c.add === "function") {
            await c.add([lidItem], 1, {}, { settle: false, open: false });
          } else {
            const res = await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ items: [lidItem] }),
            });
            if (!res.ok) throw new Error("Could not add the lids to the basket. Please try again.");
          }
        }
        if (idInput) idInput.value = String(v.id);
        const uidInput = form.querySelector("[data-line-uid]");
        if (uidInput) uidInput.value = uid;
        const deliveryInput = form.querySelector("[data-line-delivery]");
        if (deliveryInput) deliveryInput.value = deliveryLabel(v.lead_time);
        const back = form.querySelector("[data-return-to]");
        if (back) {
          const q = new URLSearchParams();
          if (walls.length > 1) q.set("wall", wall);
          q.set("added", "1");
          back.value = `${window.location.pathname}?${q.toString()}`;
        }
        // Only the cup line's own fields travel: no empty notes, no wall radio.
        if (notes) notes.disabled = !text;
        wallInputs.concat(mixInputs).forEach((input) => {
          input.disabled = true;
        });
        HTMLFormElement.prototype.submit.call(form);
        return; // the page navigates; `adding` stays on
      }

      const properties = { _uid: uid };
      const delivery = deliveryLabel(v.lead_time);
      if (delivery) properties["Lead time"] = delivery;
      if (text) properties["Design preferences"] = text;
      const items = [{ id: Number(v.id), quantity: 1, properties }];
      if (fee) items.push(fee);
      if (lidItem) items.push(lidItem);
      const cart = window.EraPackCart;
      if (cart && typeof cart.add === "function") {
        await cart.add(items);
        if (typeof cart.open === "function") cart.open();
      } else {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error("Could not add that to the cart. Please try again.");
        window.location.href = data.cartUrl || "/cart";
      }
      adding = false;
      render();
    } catch (err) {
      adding = false;
      if (notes) notes.disabled = false;
      wallInputs.concat(mixInputs).forEach((input) => {
        input.disabled = false;
      });
      render();
      const message = err && err.message ? err.message : "Could not add that to the cart. Please try again.";
      if (hintEl && /upload|file|artwork/i.test(message)) {
        artworkError = message;
        renderArtwork();
      } else {
        window.alert(message);
      }
    }
  }

  if (form) form.addEventListener("submit", addToCart);
  // Back from the cart through the bfcache after a file upload: unlock.
  /* the rough logo preview: a PNG, JPG or SVG drawn on a plain cup */
  const previewBox = root.querySelector("[data-logo-preview]");
  const previewCanvas = previewBox && previewBox.querySelector("canvas");
  let previewUrl = "";
  function showPreview(file) {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = "";
    const isImage = !!file && (/^image\/(png|jpe?g|svg\+xml)$/i.test(file.type || "") || /\.(png|jpe?g|svg)$/i.test(file.name || ""));
    if (!previewBox || !previewCanvas || !data.preview || !isImage) {
      if (previewBox) previewBox.hidden = true;
      return;
    }
    previewUrl = URL.createObjectURL(file);
    drawCupWithLogo(previewCanvas, previewUrl);
    previewBox.hidden = false;
  }
  if (fileInput) {
    fileInput.addEventListener("change", () => {
      // After readArtworkFile settles (it may swap in a smaller copy or clear
      // the input), draw whatever the input now holds.
      setTimeout(() => showPreview(fileInput.files && fileInput.files[0]), 600);
    });
  }

  /*
   * The sticky buy bar (phones and tablets): the total with VAT and the
   * Add to basket button, shown while the real button is off screen. It
   * presses the real button, so there is one add path. Hidden while the
   * basket drawer is open (CSS) and above 989px.
   */
  const stickyEl = root.querySelector("[data-sticky-bar]");
  const sticky = (() => {
    if (!stickyEl || !atc || !("IntersectionObserver" in window)) return null;
    const totalEl = stickyEl.querySelector("[data-sticky-total]");
    const btn = stickyEl.querySelector("[data-sticky-atc]");
    const mq = window.matchMedia("(max-width: 989px)");
    let atcVisible = true;
    const sync = () => {
      stickyEl.hidden = !mq.matches || atcVisible;
    };
    if (btn) btn.addEventListener("click", () => atc.click());
    new IntersectionObserver((entries) => {
      atcVisible = entries[entries.length - 1].isIntersecting;
      sync();
    }).observe(atc);
    if (mq.addEventListener) mq.addEventListener("change", sync);
    return {
      update(total) {
        if (totalEl) totalEl.textContent = `${total} with VAT`;
        if (btn) {
          btn.textContent = atc.textContent;
          btn.disabled = !!adding;
        }
      },
    };
  })();

  window.addEventListener("pageshow", (e) => {
    if (!e.persisted) return;
    adding = false;
    const notes = root.querySelector("[data-artwork-notes]");
    if (notes) notes.disabled = false;
    wallInputs.concat(mixInputs).forEach((input) => {
      input.disabled = false;
    });
    render();
  });

  render();
  renderArtwork();
}

/* ── the FAQ list (components/sections/FaqAccordion.tsx) ─────────────────── */

function setupFaq(section) {
  const items = Array.from(section.querySelectorAll(".custom-faq-item"));
  const toggles = Array.from(section.querySelectorAll("[data-faq-toggle]"));

  function setOpen(index) {
    items.forEach((item, i) => {
      const button = item.querySelector("[data-faq-toggle]");
      const panel = item.querySelector(".custom-faq-text");
      const isOpen = i === index;
      item.classList.toggle("is-open", isOpen);
      if (button) button.setAttribute("aria-expanded", isOpen ? "true" : "false");
      if (panel) panel.hidden = !isOpen;
    });
  }

  toggles.forEach((button) => {
    button.addEventListener("click", (e) => {
      // This section is handled here; keep a page-wide FAQ handler
      // (faq.js) from toggling it a second time.
      e.stopPropagation();
      const item = button.closest(".custom-faq-item");
      const i = items.indexOf(item);
      const isOpen = button.getAttribute("aria-expanded") === "true";
      setOpen(isOpen ? null : i);
    });
  });
}

function init() {
  document.querySelectorAll("[data-product-main]").forEach(setupProduct);
  document.querySelectorAll("[data-product-faq]").forEach(setupFaq);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}

// Theme editor: a section re-rendered in place needs its behaviour again.
document.addEventListener("shopify:section:load", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target) return;
  target.querySelectorAll("[data-product-main]").forEach(setupProduct);
  target.querySelectorAll("[data-product-faq]").forEach(setupFaq);
});

export { perCupLabel, penceLabel, readArtworkFile, track };
